import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateSuperuserSuperusersUserIdPatch, UserPublicWithClientLinks } from "@/client-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { cn, getErrorDetails, SUPERUSER_PERMISSIONS, superUserPermissionSchema } from "@/lib/utils";

const editSuperUserSchema = z.object({
  email: z.string().email(),
  first_name: z.string().max(255).nullable(),
  last_name: z.string().max(255).nullable(),
  isActive: z.boolean(),
  permissions: z.array(superUserPermissionSchema).min(1, "At least one permission is required"),
});

type EditSuperUserFormValues = z.infer<typeof editSuperUserSchema>;

type EditSuperUserDialogProps = Pick<
  UserPublicWithClientLinks,
  "email" | "first_name" | "last_name" | "is_active" | "client_links" | "id"
>;

export function EditSuperUserDialog({
  email,
  first_name: firstName,
  last_name: lastName,
  is_active: isActive,
  client_links,
  id,
}: EditSuperUserDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const clientLink = client_links[0];

  const form = useForm<EditSuperUserFormValues>({
    resolver: zodResolver(editSuperUserSchema),
    defaultValues: {
      email,
      first_name: firstName,
      last_name: lastName,
      isActive,
      permissions: (clientLink?.permissions ?? []) as z.infer<typeof superUserPermissionSchema>[],
    },
    mode: "onChange",
  });

  const updateSuperUserMutation = useMutation({
    mutationFn: (data: EditSuperUserFormValues) =>
      updateSuperuserSuperusersUserIdPatch({
        userId: id,
        requestBody: {
          user_in: {
            email: data.email,
            first_name: data.first_name ?? null,
            last_name: data.last_name ?? null,
            is_active: data.isActive,
            is_superuser: true,
          },
          client_links: [
            {
              // client_id is overridden server-side with the admin client
              client_id: clientLink?.client_id ?? 0,
              permissions: data.permissions,
              estimated_price_update_preferences: null,
              cc_recipients: null,
              bcc_recipients: null,
            },
          ],
        },
      }),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["superusers"] });
      toast({
        title: "Success",
        description: "Superuser updated successfully",
      });
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getErrorDetails(error, true),
        variant: "destructive",
      });
    },
  });

  async function onSubmit(data: EditSuperUserFormValues) {
    await updateSuperUserMutation.mutateAsync(data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Edit</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Superuser</DialogTitle>
          <DialogDescription>Edit the superuser details and assign their permissions.</DialogDescription>
        </DialogHeader>
        <Form {...form} key={id}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" disabled />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                      }}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>Is this superuser account active?</FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="permissions"
              render={({ field }) => (
                <FormItem>
                  <div className="mb-4">
                    <FormLabel className="flex items-center justify-between">
                      Permissions
                      {form.formState.errors.permissions && (
                        <span className="text-sm font-normal text-destructive">
                          {form.formState.errors.permissions.message}
                        </span>
                      )}
                    </FormLabel>
                    <FormDescription>Select the permissions for this superuser.</FormDescription>
                  </div>
                  <div
                    className={cn(
                      "grid grid-cols-2 gap-4",
                      form.formState.errors.permissions && "rounded-md border border-destructive/50 p-4",
                    )}
                  >
                    {SUPERUSER_PERMISSIONS.map((permission) => (
                      <FormItem key={permission.id} className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(permission.id)}
                            onCheckedChange={(checked) => {
                              const currentPermissions = field.value || [];
                              return checked
                                ? field.onChange([...currentPermissions, permission.id])
                                : field.onChange(currentPermissions.filter((value) => value !== permission.id));
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">{permission.label}</FormLabel>
                      </FormItem>
                    ))}
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={updateSuperUserMutation.isPending}>
                {updateSuperUserMutation.isPending ? "Updating..." : "Submit"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
